import { Box, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

function configuredLabel(t, configured) {
  return configured ? t('providers:health.ses.configured_yes') : t('providers:health.ses.configured_no');
}

/**
 * Safe SES health details panel — never renders secrets.
 */
export function SesHealthDetailsPanel({ details }) {
  const { t } = useTranslation(['providers', 'common']);

  if (!details) {
    return null;
  }

  const rows = [
    { label: t('providers:health.ses.region'), value: details.region || t('common:empty_value') },
    { label: t('providers:health.ses.from_address'), value: details.from_address || t('common:empty_value') },
    {
      label: t('providers:health.ses.from_source'),
      value: details.source_map?.from_address || t('common:empty_value'),
    },
    { label: t('providers:health.ses.test_recipient'), value: details.test_recipient || t('common:empty_value') },
    {
      label: t('providers:health.ses.credentials'),
      value: `${configuredLabel(t, details.key_configured)} / ${configuredLabel(t, details.secret_configured)}`,
    },
    { label: t('providers:health.ses.identity'), value: details.identity || details.identity_domain || t('common:empty_value') },
    { label: t('providers:health.ses.verification_status'), value: details.verification_status || t('common:empty_value') },
    { label: t('providers:health.ses.dkim_status'), value: details.dkim_status || t('common:empty_value') },
    {
      label: t('providers:health.ses.recipient_status'),
      value: details.recipient_status
        || (details.recipient_verified === true ? 'Success' : details.recipient_verified === false ? 'Pending' : t('common:empty_value')),
    },
  ];

  if (details.failed_step) {
    rows.push({ label: t('providers:health.ses.failed_step'), value: details.failed_step });
  }
  if (details.aws_error_code) {
    rows.push({ label: t('providers:health.ses.aws_error_code'), value: details.aws_error_code });
  }
  if (details.http_status) {
    rows.push({ label: t('providers:health.ses.http_status'), value: String(details.http_status) });
  }
  if (details.safe_error_message) {
    rows.push({ label: t('providers:health.ses.safe_error_message'), value: details.safe_error_message });
  }
  if (details.suggested_iam_action) {
    rows.push({ label: t('providers:health.ses.suggested_iam_action'), value: details.suggested_iam_action });
  }
  if (details.request_id) {
    rows.push({ label: t('providers:health.ses.request_id'), value: details.request_id });
  }

  return (
    <Box sx={{ mt: 1 }}>
      {details.is_local_placeholder_from && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          {t('providers:health.ses.local_placeholder_warning')}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {rows.map((row) => (
          <Typography key={row.label} variant="body2">
            {row.label}: <Box component="span" sx={{ fontFamily: 'monospace' }}>{row.value}</Box>
          </Typography>
        ))}
      </Stack>
      {Array.isArray(details.steps) && details.steps.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {details.steps.map((step) => (
            <ListItem key={step.step} disableGutters>
              <ListItemText
                primary={`${step.step}: ${step.status}`}
                primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

export function healthAlertSeverity(status) {
  if (status === 'ok') return 'success';
  if (status === 'warning' || status === 'skipped') return 'warning';
  if (status === 'failed') return 'error';
  return 'info';
}
